import axios from 'axios';
import { API_ROOT } from '../config/apiConfig';
import { dedupeAuditEvents } from '../utils/dedupeAuditEvents';
import { getAuthHeaders, isHttpOnlyAuthSessionEnabled } from '../utils/authSession';

export interface AuditEvent {
  id: string;
  event_type: 'submit' | 'review' | 'approve' | 'reject' | 'create' | 'update' | 'delete' | 'delegate' | 'complete' | 'login' | 'revoke' | string;
  entity_type: 'application' | 'sso_config' | 'user' | 'approval_request' | 'delegation' | string;
  entity_id: string;
  entity_name: string;
  user_id: string;
  user_name: string;
  user_role: string;
  action_description: string;
  details: Record<string, any>;
  timestamp: string;
  ip_address?: string;
  user_agent?: string;
  previous_values?: Record<string, any>;
  new_values?: Record<string, any>;
  criticality_level?: 'low' | 'medium' | 'high' | 'critical';
  application_id?: string;
  application_name?: string;
  delegated_to?: string;
  delegated_to_name?: string;
  approval_status?: 'pending' | 'approved' | 'rejected';
  comments?: string;
}

export interface AuditFilters {
  event_type?: string;
  entity_type?: string;
  user_role?: string;
  user_id?: string;
  profile_user_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  criticality_level?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

class AuditService {
  private getAuthHeaders() {
    return getAuthHeaders({ 'Content-Type': 'application/json' });
  }

  private axiosConfig() {
    return {
      headers: this.getAuthHeaders(),
      withCredentials: isHttpOnlyAuthSessionEnabled(),
    };
  }

  async logEvent(event: Omit<AuditEvent, 'id' | 'timestamp'>): Promise<void> {
    try {
      await axios.post(`${API_ROOT}/audit/events`, event, this.axiosConfig());
    } catch (error) {
      console.error('Failed to log audit event:', error);
      // Don't throw error to avoid breaking the main operation
    }
  }

  async exportSigned(dateFrom?: string, dateTo?: string): Promise<{ csv: string; signature: string; algorithm: string }> {
    const response = await axios.get(`${API_ROOT}/audit/export`, {
      ...this.axiosConfig(),
      params: { date_from: dateFrom, date_to: dateTo },
    });
    return response.data.data;
  }

  async getAuditEvents(filters: AuditFilters = {}): Promise<{ events: AuditEvent[]; total: number; pages: number }> {
    try {
      const response = await axios.get(`${API_ROOT}/audit/events`, {
        ...this.axiosConfig(),
        params: filters,
      });
      
      const backendData = response.data;
      const rawEvents: AuditEvent[] = backendData.success && backendData.data
        ? backendData.data
        : backendData.events || backendData.data || [];
      const events = dedupeAuditEvents(rawEvents);
      const rawTotal = backendData.pagination?.total ?? backendData.total ?? rawEvents.length;
      const limit = filters.limit || 20;
      const dedupedTotal = Math.max(events.length, Math.round(rawTotal * (events.length / Math.max(rawEvents.length, 1))));

      if (backendData.success && backendData.pagination) {
        return {
          events,
          total: dedupedTotal,
          pages: Math.max(1, Math.ceil(dedupedTotal / limit)),
        };
      }

      return {
        events,
        total: dedupedTotal,
        pages: Math.max(1, Math.ceil(dedupedTotal / limit)),
      };
    } catch (error) {
      console.error('Failed to fetch audit events:', error);
      throw error;
    }
  }

  async getAuditEventById(id: string): Promise<AuditEvent> {
    try {
      const response = await axios.get(`${API_ROOT}/audit/events/${id}`, this.axiosConfig());
      return response.data;
    } catch (error) {
      console.error('Failed to fetch audit event:', error);
      throw error;
    }
  }

  // Helper methods for common audit events
  async logSSOSubmission(applicationId: string, applicationName: string, userId: string, userName: string, userRole: string, details: any): Promise<void> {
    await this.logEvent({
      event_type: 'submit',
      entity_type: 'application',
      entity_id: applicationId,
      entity_name: applicationName,
      user_id: userId,
      user_name: userName,
      user_role: userRole,
      action_description: `Submitted SSO approval request for ${applicationName}`,
      details: { ...details, request_type: 'sso_approval' },
      application_id: applicationId,
      application_name: applicationName,
      criticality_level: details.criticality_level,
    });
  }

  async logSSOReview(applicationId: string, applicationName: string, userId: string, userName: string, userRole: string, action: 'approve' | 'reject', comments?: string): Promise<void> {
    const approvalStatus = action === 'approve' ? 'approved' : 'rejected';
    
    await this.logEvent({
      event_type: action,
      entity_type: 'application',
      entity_id: applicationId,
      entity_name: applicationName,
      user_id: userId,
      user_name: userName,
      user_role: userRole,
      action_description: `${action === 'approve' ? 'Approved' : 'Rejected'} SSO configuration for ${applicationName}`,
      details: { comments, action, request_type: 'sso_approval' },
      application_id: applicationId,
      application_name: applicationName,
      approval_status: approvalStatus,
      comments,
    });
  }

  async logSSOCreation(applicationId: string, applicationName: string, userId: string, userName: string, userRole: string, ssoConfig: any): Promise<void> {
    await this.logEvent({
      event_type: 'create',
      entity_type: 'sso_config',
      entity_id: applicationId,
      entity_name: `${applicationName} SSO Configuration`,
      user_id: userId,
      user_name: userName,
      user_role: userRole,
      action_description: `Created SSO configuration for ${applicationName}`,
      details: ssoConfig,
      application_id: applicationId,
      application_name: applicationName,
    });
  }

  async logSSODelegation(applicationId: string, applicationName: string, fromUserId: string, fromUserName: string, toUserId: string, toUserName: string, userRole: string, details: any): Promise<void> {
    await this.logEvent({
      event_type: 'delegate',
      entity_type: 'application',
      entity_id: applicationId,
      entity_name: applicationName,
      user_id: fromUserId,
      user_name: fromUserName,
      user_role: userRole,
      action_description: `Delegated SSO onboarding task for ${applicationName} to ${toUserName}`,
      details: { ...details, delegated_to_name: toUserName },
      application_id: applicationId,
      application_name: applicationName,
      delegated_to: toUserId,
      delegated_to_name: toUserName,
    });
  }

  async logSSOCompletion(applicationId: string, applicationName: string, userId: string, userName: string, userRole: string, details: any): Promise<void> {
    await this.logEvent({
      event_type: 'complete',
      entity_type: 'sso_config',
      entity_id: applicationId,
      entity_name: `${applicationName} SSO Configuration`,
      user_id: userId,
      user_name: userName,
      user_role: userRole,
      action_description: `Completed SSO configuration setup for ${applicationName}`,
      details,
      application_id: applicationId,
      application_name: applicationName,
    });
  }

  async logUserAction(entityType: string, entityId: string, entityName: string, userId: string, userName: string, userRole: string, action: 'create' | 'update' | 'delete', details: any): Promise<void> {
    await this.logEvent({
      event_type: action,
      entity_type: entityType as any,
      entity_id: entityId,
      entity_name: entityName,
      user_id: userId,
      user_name: userName,
      user_role: userRole,
      action_description: `${action.charAt(0).toUpperCase() + action.slice(1)} ${entityType} ${entityName}`,
      details,
    });
  }
}

export default new AuditService();
